import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, MapPin, User } from 'lucide-react';

const Guides = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const response = await fetch(`${API}/api/guides/approved`);
        if (response.ok) {
          const data = await response.json();
          setGuides(data);
        }
      } catch (error) {
        console.error('Failed to fetch guides:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, [API]);

  const getFirstName = (fullName) => {
    if (!fullName) return 'Guide';
    return fullName.split(' ')[0];
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-grow bg-gray-50 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-serif mb-4">Our Tour Guides</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Meet our amazing student tour guides who are ready to show you their campus and share their college experience with you.
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading guides...</p>
            </div>
          )}

          {/* No Guides */}
          {!loading && guides.length === 0 && (
            <div className="text-center py-12">
              <User className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No guides available at the moment.</p>
              <p className="text-gray-400 mt-2">Check back soon!</p>
            </div>
          )}

          {/* Guides Grid */}
          {!loading && guides.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map((guide) => (
                <Card 
                  key={guide.id}
                  className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                  onClick={() => navigate(`/guide/${guide.id}`)}
                  data-testid={`guide-card-${guide.id}`}
                >
                  {/* Guide Photo */}
                  <div className="h-64 bg-gray-200 relative">
                    {(guide.photo_url || (guide.photo_urls && guide.photo_urls.length > 0)) ? (
                      <img 
                        src={guide.photo_url || guide.photo_urls[0]} 
                        alt={guide.name}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: guide.photo_position || 'center' }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-20 h-20 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Price Tag */}
                    {guide.price_campus_tour && (
                      <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full shadow-md">
                        <span className="font-semibold text-green-600">${guide.price_campus_tour}</span>
                      </div>
                    )}
                  </div>

                  <CardContent className="p-5">
                    {/* Name */}
                    <h3 className="text-xl font-semibold mb-1">{guide.name}</h3>
                    
                    {/* School */}
                    <div className="flex items-center text-gray-600 mb-2">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      <span className="text-sm">{guide.school}</span>
                    </div>
                    
                    {/* Year & Major */}
                    {(guide.academic_year || guide.major) && (
                      <p className="text-sm text-gray-500 mb-3">
                        {guide.academic_year}{guide.academic_year && guide.major ? ' • ' : ''}{guide.major}
                      </p>
                    )}
                    
                    {/* Bio Preview */}
                    {guide.bio && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {guide.bio}
                      </p>
                    )}

                    {/* Tour Types */}
                    <div className="flex gap-2 mt-4">
                      {guide.tour_types?.includes('Campus Tour') && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          Campus Tour
                        </span>
                      )}
                      {(guide.tour_types?.includes('Video Chat') || guide.tour_types?.includes('Video Tour')) && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          Video Tour
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Guides;
